import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import SegmentedControl, {
  type SegmentedControlIconOption,
} from '@features/shared/components/SegmentedControl'
import { DisabledReason } from '@features/shared/components/DisabledReason'
import { settingsQueryKeys } from '../api/queryKeys'
import { getUserPreferences } from '../api/userPreferences.api'
import { useUpdateOwnPreference } from '../hooks/useUpdateOwnPreference'
import { PREFERENCE_SEGMENT_ICONS, preferenceSegmentTones } from '../logic/preferenceSegmentIcons'
import { useSystemTheme } from '../hooks/useSystemTheme'
import { findPreferenceByKey } from '../logic/preferences'
import { SETTING_DEFINITIONS, useSettingsStore, type ThemeMode } from '../stores/useSettingsStore'

const USER_PREFERENCES_STALE_MS = 5 * 60 * 1000
const LOCKED_REASON = 'Configured by Organization'

const THEME_ICONS = PREFERENCE_SEGMENT_ICONS.theme ?? {}

/**
 * The Theme choices, each with the icon and colors it has on the Preferences
 * page; System's colors are those of the theme it resolves to here.
 */
function themeOptions(systemTheme: 'light' | 'dark'): SegmentedControlIconOption<ThemeMode>[] {
  const tones = preferenceSegmentTones('theme', systemTheme)
  return (SETTING_DEFINITIONS.themeSetting.options ?? []).map((option) => ({
    value: option.value,
    label: option.label,
    icon: THEME_ICONS[option.value],
    ...tones?.[option.value],
  }))
}

/**
 * Theme (Display / Appearance) in the header, before the panel buttons: icons
 * alone ([☀][☾][▭], each named on hover; the sun yellow, the moon blue, and
 * System in whichever it resolves to), as tall as the header's buttons,
 * saved for the signed-in user at once. Locked when the organization sets the
 * theme for everyone, as on the Preferences page.
 */
export default function HeaderThemeToggle() {
  const theme = useSettingsStore((state) => state.themeSetting)
  const systemTheme = useSystemTheme()
  const options = useMemo(() => themeOptions(systemTheme), [systemTheme])
  const updateOwnPreference = useUpdateOwnPreference()
  const preferencesQuery = useQuery({
    queryKey: settingsQueryKeys.userPreferences,
    queryFn: getUserPreferences,
    staleTime: USER_PREFERENCES_STALE_MS,
  })
  const locked =
    findPreferenceByKey(preferencesQuery.data?.tabs, 'theme')?.overrides_allowed === false

  return (
    <DisabledReason reason={locked ? LOCKED_REASON : null} className="shrink-0">
      <SegmentedControl
        iconOnly
        options={options}
        value={theme}
        onChange={(next) => {
          if (next !== theme) void updateOwnPreference('theme', 'themeSetting', next)
        }}
        ariaLabel="Theme"
        disabled={locked}
        size="tile"
      />
    </DisabledReason>
  )
}
